import { Transition } from '@headlessui/react'
import { Fragment, useRef, useState } from 'react'
import { IoCloseOutline as CloseIcon } from 'react-icons/io5'
import { useShallow } from 'zustand/react/shallow'

import { Button } from '@ui/components/core'
import { PipelineList } from '@ui/components/elements'
import { Spinner } from '@ui/components/loaders'
import { CodeTransformType, DatableTrace, DatableTraceDuration } from '@ui/interfaces'
import { usePipelinesStore } from '@ui/stores'
import dayjs from 'dayjs'

import { WaterfallChart } from './WaterfallChart'

interface TraceFlyoutProps {
  show: boolean
  setShow: (show: boolean) => void
  selectedRootTrace: DatableTrace | null
  setSelectedRootTrace: (trace: DatableTrace) => void
  fullTrace: {
    traces: DatableTraceDuration[]
    totalDuration: number
  } | null
}

export const TraceFlyout = ({
  show,
  setShow,
  selectedRootTrace,
  setSelectedRootTrace,
  fullTrace,
}: TraceFlyoutProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const [showPipelineList, setShowPipelineList] = useState<boolean>(false)
  const [transformType, setTransformType] = useState<CodeTransformType>('' as CodeTransformType)

  const { pipelines } = usePipelinesStore(useShallow((state) => ({ pipelines: state.pipelines })))

  const showPipelineListDropdown = () => {
    setShowPipelineList(!showPipelineList)
    setTransformType(transformType)
  }

  return (
    <Transition
      as={Fragment}
      show={show}
      enter="transform transition ease-in-out duration-500 sm:duration-700"
      enterFrom="translate-x-full"
      enterTo="translate-x-0"
      leave="transform transition ease-in-out duration-500 sm:duration-700"
      leaveFrom="translate-x-0"
      leaveTo="translate-x-full">
      <div
        className="fixed right-0 top-0 z-50 m-0 h-full w-3/5 border-l border-gray-200 bg-white p-4 shadow-lg"
        ref={containerRef}>
        {selectedRootTrace && fullTrace ? (
          <>
            {/* Header */}
            <div className="relative mb-4 flex flex-row justify-between">
              <div className="flex flex-col space-y-1 text-gray-900">
                <h1 className="mr-5 text-2xl font-medium">Trace</h1>
                <p className="text-gray-900">{selectedRootTrace.trace_id}</p>
                <p className="text-gray-500">{dayjs(selectedRootTrace.timestamp).format('LLL')}</p>
              </div>

              <div className="flex flex-col items-end">
                <CloseIcon
                  className="h-12 w-12 cursor-pointer rounded-md p-2 hover:bg-gray-100"
                  onClick={() => setShow(false)}
                  data-testid="telemetry-details-close"
                />

                <div className="flex flex-row">
                  <Button
                    onClick={() => {
                      setTransformType(CodeTransformType.DROP)
                      setShowPipelineList(true)
                    }}>
                    Drop Trace
                  </Button>
                  <Button
                    onClick={() => {
                      setTransformType(CodeTransformType.SAMPLE)
                      setShowPipelineList(true)
                    }}>
                    Sample Trace
                  </Button>
                  <div className="relative right-0 top-12">
                    <PipelineList
                      pipelines={pipelines}
                      event={JSON.stringify(selectedRootTrace)}
                      show={showPipelineList}
                      setShowPipelineList={showPipelineListDropdown}
                      transformType={transformType}
                      serviceName={selectedRootTrace.resource['service.name']}
                      name={selectedRootTrace.name}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <p className="w-full text-right text-gray-900">
                Trace Duration: <span className="font-bold">{dayjs(fullTrace.totalDuration).format('mm:ss')}</span>s
              </p>
              <WaterfallChart
                traces={fullTrace.traces}
                totalDuration={fullTrace.totalDuration}
                setSelectedRootTrace={setSelectedRootTrace}
                ref={containerRef}
              />

              {/* Span Details */}
              <div>
                <div className="my-2 flex flex-row justify-between">
                  <p>
                    <span className="font-medium">Span</span> {selectedRootTrace.resource['service.name']}
                  </p>

                  <p>
                    <span className="font-medium">Duration</span>{' '}
                    {dayjs(selectedRootTrace.end_time).diff(dayjs(selectedRootTrace.start_time), 'ms')}
                    ms
                  </p>
                </div>

                <pre className="h-full max-h-[56vh] overflow-y-scroll border border-gray-200 bg-gray-100 p-4">
                  {JSON.stringify(selectedRootTrace, null, 2)}
                </pre>
              </div>
            </div>
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Spinner />
          </div>
        )}
      </div>
    </Transition>
  )
}
